import { useCallback, useMemo } from "react";
import { AnyAbility } from "@casl/ability";
import {
  Users,
  LucideIcon,
  LayoutDashboard,
  Globe,
  Flag,
  LayoutGrid,
  ChartNoAxesCombined,
  Building2,
  Settings,
  // Handshake,
  CreditCard,
  Shield,
  PictureInPicture,
  AtSign,
  ContactRound,
  MessagesSquare,
  // Split,
  TrendingUp,
  CircleHelp,
  ExternalLink,
  Rocket,
  Inbox,
  Bot,
  Workflow,
} from "lucide-react";

import {
  RemixiconComponentType,
  RiAdminLine,
  RiCustomerService2Line,
} from "@remixicon/react";

import { ShoprenterIcon, ShoprenterIconType } from "@/components/icons/ShoprenterIcon";

import { useTranslation } from "@/hooks/useTranslation";
import { useAbility } from "@/hooks/useAbility";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { MESSAGE_WHALE, ROOT_MENU } from "@/featureFlags";
import { useAuthContext } from "@/hooks/useAuth";
import { useAppContext } from "./hooks/useAppContext";
import { ModalType } from "./stores/modals";
import { useOrganizationStore } from "./stores/organization";
import { Organization } from "./types/organization";
import { ShopifyIcon, ShopifyIconType } from "./components/icons/ShopifyIcon";

type FeaturesCollection = Record<string, boolean>;
type IsAvailableProps = {
  ability: AnyAbility;
  features: FeaturesCollection,
  role: string,
  organization?: Organization,
};

type IsAvailable = (props: IsAvailableProps) => boolean;

export type MenuIconType =
  LucideIcon |
  RemixiconComponentType |
  ShoprenterIconType |
  ShopifyIconType;

export type Submenu = {
  href: string;
  label: string;
  icon?: MenuIconType;
  external?: boolean;
  showsModal?: ModalType;
};

type SubmenuDescription = Omit<Submenu, 'active'> & {
  isAvailable?: IsAvailable;
};

export type Menu = {
  href: string;
  label: string;
  icon: MenuIconType;
  tooltip?: boolean;
  submenus: Submenu[];
};

type MenuDescription = Omit<Menu, 'submenus'> & {
  submenus: SubmenuDescription[];
  isAvailable?: IsAvailable;
};

export type Group = {
  groupLabel: string;
  showLabelOnCollapse?: boolean;
  menus: Menu[];
};

type GroupDescription = Omit<Group, 'menus'> & {
  menus: MenuDescription[];
  isAvailable?: IsAvailable;
};

const mwMenus: GroupDescription['menus'] = [{
  href: "/mw",
  isAvailable: ({ features }) => features[MESSAGE_WHALE],
  label: "messages",
  icon: AtSign,
  submenus: [
    {
      href: "/mw/campaigns",
      label: "campaigns",
      icon: Flag,
    },
    {
      href: "/mw/lists",
      label: "lists",
      icon: ContactRound,
    },
    {
      href: "/mw/channels",
      label: "channels",
      icon: MessagesSquare,
    },
    // {
    //   href: "/mw/segments",
    //   label: "segments",
    //   icon: Split,
    // },
    {
      href: "/mw/statistics",
      label: "stats",
      icon: ChartNoAxesCombined,
    },
    {
      isAvailable: () => import.meta.hot !== undefined,
      href: "/mw/automations",
      label: "workflow",
      icon: Workflow,
    },
    {
      href: "/mw/chat-bots",
      label: "chatbots",
      icon: Bot,
      isAvailable: () => import.meta.hot !== undefined,
    },
    {
      href: "/mw/inbox",
      label: "mwInbox",
      icon: Inbox,
    }
  ],
}];

const settingsMenus: GroupDescription = {
  groupLabel: "settings",
  menus: [
    {
      href: "",
      label: "organization",
      icon: Building2,
      isAvailable: ({ ability }) => ability.can('update', 'organization'),
      submenus: [
        {
          href: "/organization/settings",
          label: "organizationSettings",
          icon: Settings,
        },
        {
          href: "/organization/team",
          label: "team",
          icon: RiAdminLine,
        },
        // {
        //   href: "/organization/access-requests",
        //   label: "accessRequests",
        //   icon: Handshake,
        // },
        {
          href: "/organization/billing",
          label: "billing",
          icon: CreditCard,
        },
        {
          href: "/organization/usage",
          label: "credits",
          icon: TrendingUp,
        },
      ]
    },
    {
      href: "/integration/shoprenter",
      label: "shoprenter.title",
      icon: ShoprenterIcon,
      isAvailable: ({ organization }) => organization?.integration === 'shoprenter',
      submenus: [],
    },
    {
      href: "/integration/shopify",
      label: "shopify.title",
      icon: ShopifyIcon,
      isAvailable: ({ organization }) => organization?.integration === 'shopify',
      submenus: [],
    },
    {
      href: "/websites",
      label: "websites",
      icon: Globe,
      submenus: [],
      isAvailable: ({ organization }) => {
        const hasPopups = organization?.billingPlan?.features
          .some(feature => feature.type === 'popups') ?? false;
  
        return hasPopups;
      },
    },
  ],
};

const vbmMenus: GroupDescription[] = [
  {
    groupLabel: "",
    menus: [
      {
        href: "/dashboard",
        label: "dashboard",
        icon: LayoutDashboard,
        submenus: []
      },
    ]
  },
  {
    groupLabel: "messages",
    showLabelOnCollapse: false,
    menus: mwMenus[0].submenus.map(({ href, label, icon, isAvailable }) => ({
      href,
      label,
      icon: icon ?? LayoutGrid,
      isAvailable,
      submenus: [],
    })),
  },
  // {
  //   groupLabel: "popup",
  //   // showLabelOnCollapse: false,
  //   menus: [
  //     {
  //       href: "/campaigns",
  //       label: "campaigns",
  //       icon: Flag,
  //       isAvailable: () => true,
  //       submenus: [],
  //     },
  //     {
  //       href: "/subscribers",
  //       label: "subscribers",
  //       icon: Users,
  //       isAvailable: () => true,
  //       submenus: [],
  //     },
  //     {
  //       href: "/popups",
  //       label: "library",
  //       icon: LayoutGrid,
  //       isAvailable: () => true,
  //       submenus: [],
  //     },
  //     {
  //       href: "/statistics",
  //       label: "stats",
  //       icon: ChartNoAxesCombined,
  //       isAvailable: () => true,
  //       submenus: [],
  //     },
  //   ],
  // },
  {
    groupLabel: "",
    isAvailable: ({ organization }) => {
      const hasPopups = organization?.billingPlan?.features
        .some(feature => feature.type === 'popups') ?? false;

      return hasPopups;
    },
    menus: [
      {
        href: "/popups",
        label: "popups",
        icon: PictureInPicture,
        submenus: [
          {
            href: "/campaigns",
            label: "campaigns",
            icon: Flag,
          },
          {
            href: "/subscribers",
            label: "subscribers",
            icon: Users,
          },
          {
            href: "/popups",
            label: "library",
            icon: LayoutGrid,
          },
          {
            href: "/statistics",
            label: "stats",
            icon: ChartNoAxesCombined,
          },
        ],
      },
    ]
  },
  settingsMenus,
];

const appMenuItems: GroupDescription[] = [
  {
    groupLabel: "",
    menus: [
      {
        href: "/dashboard",
        label: "dashboard",
        icon: LayoutDashboard,
        submenus: []
      },
      // {
      //   href: "/websites",
      //   label: "websites",
      //   icon: Globe,
      //   submenus: [],
      // },
    ]
  },
  {
    groupLabel: "",
    menus: [
      {
        href: "/popups",
        label: "popups",
        icon: PictureInPicture,
        submenus: [
          {
            href: "/campaigns",
            label: "campaigns",
            icon: Flag,
          },
          {
            href: "/subscribers",
            label: "subscribers",
            icon: Users,
          },
          {
            href: "/popups",
            label: "library",
            icon: LayoutGrid,
          },
          {
            href: "/statistics",
            label: "stats",
            icon: ChartNoAxesCombined,
          },
        ],
      },
      ...mwMenus,
    ]
  },
  settingsMenus,
];

const appMenus: Record<string, GroupDescription[]> = {
  'viber-uzenetek': vbmMenus,
  default: appMenuItems,
};

const bottomMenuItems: GroupDescription[] = [
  // {
  //   groupLabel: "",
  //   isAvailable: ({ features, role }) => features[ROOT_MENU] && role === 'admin',
  //   menus: [
  //     {
  //       href: "",
  //       label: "Root",
  //       icon: Shield,
  //       submenus: [
  //         {
  //           href: "/root/pre-registrations",
  //           label: "Pre Registrations",
  //         },
  //       ]
  //     },
  //   ]
  // },
  {
    groupLabel: "other",
    showLabelOnCollapse: false,
    menus: [
      {
        href: "",
        label: "help",
        icon: CircleHelp,
        tooltip: true,
        submenus: [
          {
            href: "",
            showsModal: "showVbmQuickstart",
            icon: Rocket,
            label: "quickstart",
          },
          {
            href: "https://docs.viberuzenetek.hu",
            icon: ExternalLink,
            external: true,
            label: "documentation",
          },
          {
            href: "/support",
            icon: RiCustomerService2Line,
            label: "support",
          }
        ]
      },
    ]
  },
];

export function useGetMenuList(): { appMenu: Group[]; bottomMenu: Group[] } {
  const { t } = useTranslation('common');
  const { user } = useAuthContext();
  const ability = useAbility();
  const { appType } = useAppContext();

  const organization = useOrganizationStore(state => state.organization) as Organization;
  const isRootMenuEnabled = useFeatureIsOn(ROOT_MENU);
  const isMessageWhaleEnabled = useFeatureIsOn(MESSAGE_WHALE);

  const features = useMemo(() => ({
    [ROOT_MENU]: isRootMenuEnabled,
    [MESSAGE_WHALE]: isMessageWhaleEnabled,
  })
  , [isRootMenuEnabled, isMessageWhaleEnabled]);

  const checkAvailability = useCallback((isAvailable?: IsAvailable) => 
    isAvailable ? isAvailable({ ability, features, role: user.role, organization }) : true,
    [ability, features, user.role, organization]
  );

  const transformMenu = useCallback((menu: GroupDescription[]) => menu
    .filter(({ isAvailable }) => checkAvailability(isAvailable))
    .map(({ groupLabel, showLabelOnCollapse, menus }) => ({
      groupLabel: t(groupLabel),
      showLabelOnCollapse,
      menus: menus
      .filter(({ isAvailable }) => checkAvailability(isAvailable))
      .map(({ href, label, icon, tooltip, submenus }) => ({
        href,
        label: t(label),
        icon,
        tooltip,
        submenus: submenus
          .filter(({ isAvailable }) => checkAvailability(isAvailable))
          .map(({ href, label, icon, external, showsModal }) => ({
            href,
            label: t(label),
            icon,
            external,
            showsModal,
          })),
      }))
    })),
    [t, checkAvailability]
  );

  const appMenu = useMemo(
    () => transformMenu(appMenus[appType] ?? appMenus.default),
    [transformMenu, appType]
  );
  
  const bottomMenu = useMemo(
    () => transformMenu(bottomMenuItems),
    [transformMenu]
  );

  return {
    appMenu,
    bottomMenu,
  };
}
